import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Trophy, Loader2, Send, MessageSquare, Download } from "lucide-react";
import { toast, Toaster } from "sonner";

const KYC_BADGE = {
  verified: "badge-success",
  in_review: "badge-review",
  rejected: "badge-rejected",
  pending: "badge-pending",
};

const DEFAULT_MSG =
  "Asante sana! Your Fortiqa referrals have unlocked free cover for fellow merchants — and an extra free week for you. Keep sharing. 🎁";

export default function ReferralLeaderboard() {
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [composing, setComposing] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MSG);
  const [sending, setSending] = useState(false);

  function load() {
    api.get("/admin/referrals/leaderboard", { params: { limit: 20 } })
      .then((r) => setData(r.data));
  }
  useEffect(load, []);

  function toggle(id, on) {
    setChecked((p) => {
      const n = new Set(p);
      if (on) n.add(id); else n.delete(id);
      return n;
    });
  }

  async function downloadCsv() {
    try {
      const { data: blob } = await api.get("/admin/referrals/leaderboard.csv", { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fortiqa_referral_leaderboard_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function sendThankYou() {
    setSending(true);
    try {
      const body = (message || "").trim();
      const { data: r } = await api.post("/admin/referrals/thank-you", {
        merchant_ids: Array.from(checked),
        message: body || undefined,
      });
      toast.success(`Thank-you sent to ${r.sent_count}/${r.attempted} merchant${r.attempted === 1 ? "" : "s"}`);
      setComposing(false);
      setChecked(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  }

  if (!data) {
    return (
      <div className="p-8 text-[#5c6360]" data-testid="referral-leaderboard-loading">
        <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Building leaderboard…
      </div>
    );
  }

  const { leaderboard, totals } = data;

  return (
    <div className="p-8 space-y-6 max-w-6xl" data-testid="referral-leaderboard-page">
      <Toaster position="top-right" />
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#c15337]">Retention</div>
          <h1 className="font-heading text-3xl font-bold mt-1 text-[#114232] flex items-center gap-2">
            <Trophy className="w-7 h-7 text-[#d49a36]" /> Referral Leaderboard
          </h1>
          <p className="text-sm text-[#5c6360] mt-1 max-w-xl">
            Top merchants spreading the word. Use this for targeted thank-you WhatsApp
            blasts and quarterly partner rebates.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 min-w-[260px]">
          <div className="metric-card !p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">Total rewarded</div>
            <div className="text-2xl font-bold font-mono mt-1.5">{totals.total_rewarded}</div>
          </div>
          <div className="metric-card !p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">Total invites</div>
            <div className="text-2xl font-bold font-mono mt-1.5">{totals.total_referred}</div>
          </div>
        </div>
      </header>

      <div className="flex items-center gap-3 flex-wrap" data-testid="leaderboard-bulk-controls">
        <span className="text-xs text-[#5c6360] font-mono">{checked.size} selected</span>
        <Button
          onClick={() => setComposing(true)}
          disabled={checked.size === 0}
          data-testid="thank-you-open-btn"
          className="btn-terracotta rounded-full px-4 h-9 text-xs uppercase tracking-wider hover:bg-[var(--terracotta-hover)]"
        >
          <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Send thank-you (WhatsApp)
        </Button>
        <Button
          onClick={downloadCsv}
          data-testid="leaderboard-csv-btn"
          variant="outline"
          className="rounded-full px-4 h-9 text-xs uppercase tracking-wider"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" /> Download CSV
        </Button>
      </div>

      {composing && (
        <div className="bg-white border border-[#114232]/15 rounded-md p-5 space-y-3" data-testid="thank-you-composer">
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">
            Compose · {checked.size} recipient{checked.size === 1 ? "" : "s"}
          </div>
          <Textarea
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={800}
            data-testid="thank-you-message"
            className="border-[#114232]/15"
          />
          <div className="flex items-center gap-2 justify-end">
            <Button variant="ghost" onClick={() => setComposing(false)} data-testid="thank-you-cancel">
              Cancel
            </Button>
            <Button
              onClick={sendThankYou}
              disabled={sending || checked.size === 0}
              data-testid="thank-you-send"
              className="btn-baobab rounded-full px-5 h-10 hover:bg-[var(--baobab-hover)]"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send broadcast
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="leaderboard-table">
          <thead className="text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="text-left font-medium py-3 px-3 w-10"></th>
              <th className="text-left font-medium py-3 px-5">#</th>
              <th className="text-left font-medium py-3 px-5">Merchant</th>
              <th className="text-left font-medium py-3 px-5">Code</th>
              <th className="text-left font-medium py-3 px-5">KYC</th>
              <th className="text-left font-medium py-3 px-5">Score</th>
              <th className="text-left font-medium py-3 px-5">Rewarded</th>
              <th className="text-left font-medium py-3 px-5">Invited</th>
              <th className="text-left font-medium py-3 px-5">Conv.</th>
              <th className="text-right font-medium py-3 px-5">Free weeks</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((row) => (
              <tr key={row.merchant_id} className="border-t border-border" data-testid={`leaderboard-row-${row.merchant_id}`}>
                <td className="py-3 px-3">
                  <input
                    type="checkbox"
                    checked={checked.has(row.merchant_id)}
                    onChange={(e) => toggle(row.merchant_id, e.target.checked)}
                    data-testid={`leaderboard-check-${row.merchant_id}`}
                    className="h-4 w-4 accent-[#c15337] cursor-pointer"
                  />
                </td>
                <td className="py-3 px-5 font-mono">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                    row.rank === 1 ? "bg-[#d49a36]/20 text-[#8a5a16]" :
                    row.rank <= 3 ? "bg-[#114232]/12 text-[#114232]" :
                    "bg-[#114232]/5 text-[#5c6360]"
                  }`}>
                    {row.rank}
                  </span>
                </td>
                <td className="py-3 px-5">
                  <div className="font-medium">{row.business_name || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{row.phone}</div>
                </td>
                <td className="py-3 px-5 font-mono text-xs tracking-widest text-[#114232]">{row.referral_code}</td>
                <td className="py-3 px-5">
                  <span className={`badge-status ${KYC_BADGE[row.kyc_status] || "badge-pending"}`}>
                    {row.kyc_status?.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="py-3 px-5 font-mono">{row.risk_score ?? "—"}</td>
                <td className="py-3 px-5 font-mono font-bold">{row.rewarded_referrals}</td>
                <td className="py-3 px-5 font-mono">{row.invited_total}</td>
                <td className="py-3 px-5 font-mono">{row.conversion_pct}%</td>
                <td className="py-3 px-5 text-right">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#d49a36]/12 text-[#8a5a16] font-mono text-xs">
                    <Send className="w-3 h-3" /> {row.free_weeks_credit}
                  </span>
                </td>
              </tr>
            ))}
            {leaderboard.length === 0 && (
              <tr>
                <td colSpan={10} className="py-10 text-center text-muted-foreground">
                  No referral rewards yet — share the program with your top merchants to seed it.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
